// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formApi,
  FormFieldType,
  FormSubmissionStatus,
  GetFormDto,
} from 'src/api/form';
import { organizationProfileApi } from 'src/api/organization-profile';
import { reminderApi } from 'src/api/reminder';
import { visitApi } from 'src/api/visit';
import { getFileUrl } from 'src/utils/getFileUrl';
import { openVisitAttachment } from 'src/utils/openVisitAttachment';
import { isVisitEditWindowOpen } from 'src/utils/visitEditWindow';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import { useUserContext } from 'src/auth/useUserContext';
import AiFeatureLabel from 'src/components/ai-feature-label/AiFeatureLabel';
import Iconify from 'src/components/iconify';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { getFileIcon } from 'src/sections/@dashboard/profile/components/ProfileFilesTable';
import VisitTranscriptionContent from 'src/sections/@dashboard/visit/components/VisitTranscriptionContent';
import { Roles } from 'src/types/User';
import {
  GetVisitById,
  GetVisitByIdStatusEnum,
  VisitActivityResponseDtoActionEnum,
  VisitActivityResponseDtoTypeEnum,
} from 'woofwoof-api';
import OrganizationVisitModalNotesTabs, {
  StyledMarkdown,
} from './OrganizationVisitModalNotesTabs';
import VisitNotesDraftHint from './VisitNotesDraftHint';
import VisitNotesStatusBadge, {
  resolveVisitNotesStatus,
} from 'src/components/visit-status-badge/VisitNotesStatusBadge';
import hasRichTextContent from 'src/utils/hasRichTextContent';
import OrganizationVisitModalAssistant from './OrganizationVisitModalAssistant';
import { useVisitModalAction } from './VisitModalActionContext';
import { useVisitAssistantAction } from './VisitAssistantActionContext';
import {
  VISIT_MODAL_ASSISTANT_QUERY,
  VISIT_MODAL_PAPER_MEDIA,
  VISIT_MODAL_PAPER_QUERY,
} from './visitModalLayout';
import { useSeededEditorValue } from 'src/hooks/useSeededEditorValue';
import { visitDetailsKey } from 'src/sections/@dashboard/visit/utils/visitDetailsQuery';
import { useClinicalQuery } from 'src/sections/@dashboard/visit/hooks/useClinicalQuery';
import { OrgVisitModalTab } from './orgVisitModalTabs';
import { isVisitAssistantEligible } from './assistantEligibility';
import { isVisitSellable } from './saleEligibility';
import { AssistantAction } from 'src/sections/@dashboard/visit/utils/assistantActions';
import { markdownToEditorHtml } from 'src/utils/markdownToEditorHtml';
import { selectOwnerBookingDocuments } from 'src/utils/ownerBookingDocuments';
import { useCanStartSale } from 'src/sections/@dashboard/warehouse/hooks/useCanStartSale';
import VisitAddMenu from './VisitAddMenu';
import VisitModalActivityTab from './VisitModalActivityTab';
import VisitModalDocumentsTab from './VisitModalDocumentsTab';
import VisitModalMedicationsTab from './VisitModalMedicationsTab';
import VisitModalRemindersTab from '../../../visit/components/VisitModalRemindersTab';
import VisitModalTreatmentsTab from './VisitModalTreatmentsTab';
import { translateKey } from 'src/locales/translate';

// GetVisitById types `documents` as Array<object>; this narrows the fields we
// read off each aggregated document (incl. uploadedByRole, which the API carries
// at runtime but doesn't declare).
type VisitBookingDocument = {
  filename: string;
  url?: string;
  originalFilename?: string;
  uploadedAt?: string | Date;
  createdAt?: string | Date;
  uploadedByRole?: 'OWNER' | 'STAFF';
};

type OrganizationVisitModalDetailsTabsProps = {
  visit: GetVisitById;
  onVisitDataChange?: () => void;
};

export default function OrganizationVisitModalDetailsTabs({
  visit,
  onVisitDataChange,
}: OrganizationVisitModalDetailsTabsProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Re-sign on open rather than trusting the stored doc.url (short-lived signed
  // URL / not always an absolute URL), mirroring the "Dokumenty" tab opener.
  const openAttachment = (filename: string) =>
    openVisitAttachment({ visitId: visit.id, filename }).catch(() =>
      enqueueSnackbar(
        translateKey('hardcoded.nie_udalo_sie_otworzyc_dokumentu'),
        { variant: 'error' },
      ),
    );
  const { data: userData } = useUserContext();
  const { setAction } = useVisitModalAction();
  const {
    setPatientNotesDraft,
    setInternalNotesDraft,
    registerAssistantActionHandler,
  } = useVisitAssistantAction();
  const isVisitCompleted = visit.status === GetVisitByIdStatusEnum.Completed;
  const [value, setValue] = useState(OrgVisitModalTab.Complaints);
  const isVisitRejected = visit.status === GetVisitByIdStatusEnum.Rejected;
  // Patient notes belong to a visit that took place: a completed one, or an
  // aborted one (it started). A cancelled visit keeps only internal notes.
  const takesPatientNotes =
    isVisitCompleted || visit.status === GetVisitByIdStatusEnum.Aborted;
  const {
    selectedRole,
    isOrganizationAdminRoleSelected,
    selectedOrganization,
  } = useOrganizationContext();
  const isProfessionalRole = selectedRole === Roles.professional;

  // Access control
  const visitProfessionalId =
    (visit.professional as any)?._id || (visit.professional as any)?.id;
  const visitOrganizationId =
    (visit.organization as any)?._id || (visit.organization as any)?.id;
  const isVisitProfessional =
    isProfessionalRole && userData?.id === visitProfessionalId;
  const isSameOrg = selectedOrganization?.id === visitOrganizationId;
  // A completed visit can be edited only within the 30-day post-visit window;
  // afterwards the whole modal goes read-only (silent — affordances disappear).
  // Non-completed visits are unaffected (the helper returns true for them).
  const isEditWindowOpen = isVisitEditWindowOpen(visit.status, visit.endTime);
  // When the viewport is too small for the assistant side panel, surface the
  // assistant as the first tab instead (mutually exclusive with the panel,
  // which the modal renders when assistantFits). isPaperMode decides whether the
  // tab's chat fills the body (and scrolls internally) or keeps its own 60vh
  // scroll in body-scroll mode.
  const assistantFits = useMediaQuery(VISIT_MODAL_ASSISTANT_QUERY);
  const isPaperMode = useMediaQuery(VISIT_MODAL_PAPER_QUERY);
  // The Asystent Vetsi tab is professional-only — the organization-admin role
  // never sees it (mirrors the side-panel gate in OrganizationVisitModal).
  const showAssistantTab =
    isVisitAssistantEligible(visit.status) &&
    isProfessionalRole &&
    isSameOrg &&
    isEditWindowOpen &&
    !assistantFits;
  // If the viewport grows past the panel breakpoint while the assistant tab is
  // active, the tab disappears (the side panel takes over) — fall back to the
  // first content tab so the body isn't left blank.
  useEffect(() => {
    if (value === OrgVisitModalTab.Assistant && !showAssistantTab) {
      setValue(OrgVisitModalTab.Complaints);
    }
  }, [value, showAssistantTab]);
  const canChangeMedicationStatus =
    isVisitProfessional &&
    (isVisitCompleted || visit.status === GetVisitByIdStatusEnum.Pending);
  const canEditVisitData = canChangeMedicationStatus && isEditWindowOpen;
  const canExportPdf =
    isSameOrg && (isProfessionalRole || !!isOrganizationAdminRoleSelected);
  const canSeePrices = isSameOrg;
  // A Sale needs no edit window and no visit vet: any member acting in the
  // visit's organization may sell while its Warehouse is enabled (R1) — but only
  // in a visit in progress or completed (#716, #769; the backend refuses it too).
  const canStartSale = useCanStartSale();
  const profileId = (visit.profile as any)?.id || (visit.profile as any)?._id;
  const transcription = (visit as any).transcription ?? null;
  const hasTranscription =
    Array.isArray(transcription?.segments) && transcription.segments.length > 0;
  const queryClient = useQueryClient();

  const handleActivityChange = useCallback(() => {
    queryClient.invalidateQueries(['visit-activity-modal', visit.id]);
    // Visit-scoped keys — single source of truth for meds/treatments/docs
    // now that the Document/Medication/TreatmentRecord collections each
    // carry `visitId` and the visit-scoped list endpoints query by it.
    queryClient.invalidateQueries(['visit-medications-modal', visit.id]);
    queryClient.invalidateQueries(['visit-treatments-modal', visit.id]);
    queryClient.invalidateQueries(['visit-reminders-modal', visit.id]);
    // The Reminders tab gate (this component) and the tab's own content
    // (VisitModalRemindersTab) share the `visit-reminders` key — invalidate it
    // too so the tab appears immediately after adding the first reminder,
    // without needing a modal reopen.
    queryClient.invalidateQueries(['visit-reminders', visit.id]);
    // Refresh the visit-scoped documents list (and the Dokumenty tab gate).
    queryClient.invalidateQueries(['visit-documents-modal', visit.id]);
    queryClient.invalidateQueries(['visitById', visit.id]);
    // Profile-scoped reminders key is still used by the reminders UI that
    // lists across visits; keep it around for correctness.
    queryClient.invalidateQueries(['profile-reminders-modal', profileId]);
    onVisitDataChange?.();
  }, [queryClient, visit.id, profileId, onVisitDataChange]);

  // An unsent patient-notes draft: saved when the visit was ended, kept for the
  // clinic only. The API sends `notesDraft` to the owning organisation only, and
  // the draft is superseded the moment real `notes` exist. Read straight off the
  // visit object (like `hasNotes` below) so the local mutation done after a
  // successful send clears the draft state on the next render.
  const notesDraft = visit.notesDraft?.trim() ? visit.notesDraft : '';
  const isDraftPending = !!notesDraft && !visit.notes;

  // Notes editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const seededNotes = visit.notes || visit.notesDraft || '';
  const seededInternalNotes = (visit as any).internalNotes || '';
  const [notes, setNotes] = useState<string>(seededNotes);
  const [internalNotes, setInternalNotes] =
    useState<string>(seededInternalNotes);
  // The draft only lands once the by-id fetch resolves, after these were
  // seeded — fill the editors then, unless the professional has already typed.
  useSeededEditorValue(seededNotes, notes, setNotes);
  useSeededEditorValue(seededInternalNotes, internalNotes, setInternalNotes);
  // Snapshot of the patient notes taken right before the assistant replaced them,
  // so the professional can revert the last AI change with one click.
  const [patientNotesSnapshot, setPatientNotesSnapshot] = useState<
    string | null
  >(null);

  // Finishing an unsent draft is authorised by organisation membership (the
  // backend's send-draft endpoint is not author-only), so an organisation admin
  // may finish it too — otherwise the draft could be seen but never sent.
  const canFinishNotesDraft =
    isDraftPending &&
    takesPatientNotes &&
    isSameOrg &&
    (isProfessionalRole || !!isOrganizationAdminRoleSelected) &&
    isEditWindowOpen;
  const canEditPatientNotes =
    (isProfessionalRole && takesPatientNotes && isEditWindowOpen) ||
    canFinishNotesDraft;
  const canEditInternalNotes =
    (isProfessionalRole || isOrganizationAdminRoleSelected) &&
    !isVisitRejected &&
    isEditWindowOpen;
  const canEdit =
    value === OrgVisitModalTab.InternalNotes
      ? canEditInternalNotes
      : canEditPatientNotes;
  const isNotesTab =
    value === OrgVisitModalTab.PatientNotes ||
    value === OrgVisitModalTab.InternalNotes;

  const applyPatientNotesAction = useCallback(
    (action: AssistantAction) => {
      setValue(OrgVisitModalTab.PatientNotes);
      setIsEditing(true);
      // Snapshot the pre-replacement notes so the change can be reverted.
      setPatientNotesSnapshot(notes);
      setNotes(markdownToEditorHtml(action.payload.notes));
    },
    [notes],
  );

  const applyInternalNotesAction = useCallback((action: AssistantAction) => {
    setValue(OrgVisitModalTab.InternalNotes);
    setIsEditing(true);
    setInternalNotes(markdownToEditorHtml(action.payload.notes));
  }, []);

  const handleAssistantAction = useCallback(
    (action: AssistantAction) => {
      if (action.type === 'fill_patient_notes') {
        applyPatientNotesAction(action);
      } else if (action.type === 'fill_internal_notes') {
        applyInternalNotesAction(action);
      }
    },
    [applyPatientNotesAction, applyInternalNotesAction],
  );

  const handleRevertPatientNotes = useCallback(() => {
    if (patientNotesSnapshot === null) return;
    setNotes(patientNotesSnapshot);
    setPatientNotesSnapshot(null);
  }, [patientNotesSnapshot]);

  const handleNotesCancel = useCallback(() => {
    // Fall back to the unsent draft so cancelling doesn't look like the draft
    // was lost.
    setNotes(visit.notes || visit.notesDraft || '');
    setInternalNotes((visit as any).internalNotes || '');
    setIsEditing(false);
    setPatientNotesSnapshot(null);
  }, [visit]);
  const handlePatientNotesSave = useCallback(async () => {
    try {
      setIsSubmitting(true);
      // Publishing an unsent draft goes through the org-scoped endpoint (any
      // member of the owning organisation may finish it); ordinary edits keep
      // using the author-only one.
      if (isDraftPending) {
        await visitApi.visitControllerSendDraftNotes({
          visitId: visit.id,
          addPatientNotesDto: { notes },
        });
      } else {
        await visitApi.visitControllerAddPatientNotes({
          visitId: visit.id,
          addPatientNotesDto: { notes },
        });
      }
      enqueueSnackbar(
        translateKey('hardcoded.notatki_zostaly_zaktualizowane'),
        { variant: 'success' },
      );
      setIsEditing(false);
      setPatientNotesSnapshot(null);
      visit.notes = notes;
      visit.notesDraft = undefined;
      visit.hasNotesDraft = false;
      // The modal reads the visit by id; without this the next render would
      // rehydrate the draft we just published from the cached response.
      queryClient.invalidateQueries(visitDetailsKey(visit.id));
      onVisitDataChange?.();
    } catch (error) {
      enqueueSnackbar(
        translateKey('hardcoded.wystapil_blad_podczas_aktualizacji_notatek'),
        {
          variant: 'error',
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    visit,
    notes,
    isDraftPending,
    enqueueSnackbar,
    onVisitDataChange,
    queryClient,
  ]);

  const handleInternalNotesSave = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await visitApi.visitControllerAddInternalNotes({
        visitId: visit.id,
        addInternalNotesDto: { internalNotes },
      });
      enqueueSnackbar(
        translateKey('hardcoded.notatki_wewnetrzne_zostaly_zaktualizowane'),
        {
          variant: 'success',
        },
      );
      setIsEditing(false);
      setPatientNotesSnapshot(null);
      (visit as any).internalNotes = internalNotes;
      onVisitDataChange?.();
    } catch (error) {
      enqueueSnackbar(
        translateKey('hardcoded.wystapil_blad_podczas_aktualizacji_notatek'),
        {
          variant: 'error',
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [visit, internalNotes, enqueueSnackbar, onVisitDataChange]);

  useEffect(() => {
    if (!isNotesTab || !canEdit) {
      // Only the Treatments tab still owns the per-tab `action` slot (its
      // "Generuj PDF" export button); every other tab's add/edit affordance now
      // lives in the global "Dodaj do wizyty" menu (addAction). Clear any stale
      // `action` for all non-Treatments tabs.
      if (value !== OrgVisitModalTab.Treatments) {
        setAction(null);
      }
      return;
    }
    // On the notes tabs the footer only shows the save/cancel pair while
    // editing. Entering edit mode is done from the "Dodaj do wizyty" menu
    // (Notatki dla pacjenta / Notatki wewnętrzne), so there's no "Edytuj
    // notatki" button anymore.
    setAction?.(
      isEditing ? (
        <Stack direction="row" spacing={1}>
          <Button
            size="medium"
            variant="outlined"
            color="inherit"
            onClick={handleNotesCancel}
            disabled={isSubmitting}
          >
            {translateKey('common.cancel')}
          </Button>
          <LoadingButton
            size="medium"
            variant="contained"
            color="primary"
            onClick={
              value === OrgVisitModalTab.PatientNotes
                ? handlePatientNotesSave
                : handleInternalNotesSave
            }
            loading={isSubmitting}
          >
            {isDraftPending && value === OrgVisitModalTab.PatientNotes
              ? translateKey('visit.notesDraftSendButton')
              : translateKey('visit.saveInternalNotes')}
          </LoadingButton>
        </Stack>
      ) : null,
    );
  }, [
    isNotesTab,
    canEdit,
    isDraftPending,
    isEditing,
    isSubmitting,
    handleNotesCancel,
    handlePatientNotesSave,
    handleInternalNotesSave,
    value,
    setAction,
  ]);

  // Publish the live patient-notes draft to the assistant context so Vetsi can
  // read the current value when composing suggestions.
  useEffect(() => {
    setPatientNotesDraft(notes);
  }, [notes, setPatientNotesDraft]);

  // Publish the live internal-notes draft too, so the assistant can read what
  // the professional is currently writing in Notatki wewnętrzne.
  useEffect(() => {
    setInternalNotesDraft(internalNotes);
  }, [internalNotes, setInternalNotesDraft]);

  // Register / unregister the handler that lets the assistant fill the notes
  // editor with one action dispatch.
  useEffect(() => {
    registerAssistantActionHandler(handleAssistantAction);
    return () => registerAssistantActionHandler(null);
  }, [handleAssistantAction, registerAssistantActionHandler]);

  // Forms data
  const { data: submissionsResponse, isLoading: isSubmissionsLoading } =
    useQuery(['visit-submissions', visit.id], () =>
      formApi.getSubmissionsByVisit(visit.id),
    );
  const submissions = submissionsResponse?.data ?? [];
  const submittedSubmissions = submissions.filter(
    (s) => s.status === FormSubmissionStatus.SUBMITTED,
  );
  const formIds = Array.from(
    new Set(submittedSubmissions.map((s) => s.formId)),
  );
  const { data: formsData, isLoading: isFormsLoading } = useQuery(
    ['visit-forms', ...formIds],
    () => Promise.all(formIds.map((id) => formApi.getFormById(id))),
    { enabled: formIds.length > 0 },
  );
  const formsMap = new Map<string, GetFormDto>();
  if (formsData) {
    for (const res of formsData) {
      if (res?.data) formsMap.set(res.data.id, res.data);
    }
  }
  const hasSubmissions = submittedSubmissions.length > 0;
  const hasNotes = !!(visit as any)?.notes;
  const hasInternalNotes = !!(visit as any)?.internalNotes;

  // Activity data
  const { data: activityData } = useQuery(
    ['visit-activity-modal', visit.id],
    () =>
      organizationProfileApi.organizationProfileControllerGetVisitActivity({
        visitId: visit.id,
      }),
    { enabled: isVisitCompleted },
  );
  const activityItems = useMemo(() => activityData?.data || [], [activityData]);

  // Medication, TreatmentRecord and Document each have their own collection
  // keyed by `visitId`. Query them scoped to this visit rather than pulling
  // the profile's full list and filtering by activity ids — the collection
  // IS the source of truth, activity is just an audit log. The same endpoint
  // works for guest visits (where profileId is null) and authed visits.
  // R1-1: both answers carry the viewing clinic's stock issues and
  // materials, and this modal also opens another clinic's visit, so the
  // keys are the viewing clinic's.
  const { data: medicationsData } = useClinicalQuery(
    'visit-medications-modal',
    visit.id,
    [],
    (options) => visitScopedPrescribingApi.listMedications(visit.id, options),
    { enabled: isVisitCompleted },
  );
  const { data: treatmentsData } = useClinicalQuery(
    'visit-treatments-modal',
    visit.id,
    [],
    (options) => visitScopedPrescribingApi.listTreatments(visit.id, options),
    { enabled: isVisitCompleted },
  );
  // Query visit documents (instead of reading the prop's aggregated
  // visit.documents) so the Dokumenty tab gate refreshes when a document is
  // added — e.g. the generated summary PDF — via cache invalidation.
  const { data: documentsData } = useQuery(
    ['visit-documents-modal', visit.id],
    () => visitScopedPrescribingApi.listDocuments(visit.id),
    { enabled: isVisitCompleted },
  );

  const visitMedications = useMemo(
    () => medicationsData?.data || [],
    [medicationsData],
  );
  const visitTreatments = useMemo(
    () => treatmentsData?.data || [],
    [treatmentsData],
  );

  // Visit-scoped reminders. Mirrors the medication/treatment fetch — the
  // tab gate uses `.length > 0`, and the tab itself re-uses the same
  // query via TanStack's cache.
  const { data: remindersData } = useQuery(
    ['visit-reminders', visit.id],
    () =>
      reminderApi.reminderControllerGetVisitReminders({ visitId: visit.id }),
    { enabled: isVisitCompleted },
  );
  const visitReminders = useMemo(
    () => remindersData?.data || [],
    [remindersData],
  );

  // Documents come from the visit-scoped documents endpoint (queried above) so
  // the tab gate and content refresh on invalidation. Newest first.
  const visitDocuments = useMemo(() => {
    const docs = (documentsData?.data || []).slice();
    return docs.sort((a, b) => {
      const dateA = new Date(a.uploadedAt || 0).getTime();
      const dateB = new Date(b.uploadedAt || 0).getTime();
      return dateB - dateA;
    });
  }, [documentsData]);

  // The Dolegliwości tab lists only the owner's *booking* attachments. Anything
  // added clinic-side during/after the visit (the generated summary, uploads from
  // the Dokumenty tab) carries uploadedByRole === 'STAFF' and lives in the
  // Dokumenty tab — exclude it here. Match OWNER explicitly (treating a missing
  // role as OWNER) so every non-owner value is excluded, including the legacy
  // 'PROFESSIONAL'; legacy docs without the field were owner booking uploads, kept.
  const bookingDocuments = useMemo<VisitBookingDocument[]>(() => {
    return selectOwnerBookingDocuments(
      visit.documents as VisitBookingDocument[] | undefined,
    ).sort((a, b) => {
      const dateA = new Date(a.uploadedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.uploadedAt || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [visit.documents]);

  const { mutate: removeActivity } = useMutation({
    mutationFn: (activityId: string) =>
      organizationProfileApi.organizationProfileControllerRemoveVisitActivity({
        visitId: visit.id,
        activityId,
      }),
    onSuccess: () => {
      handleActivityChange();
      enqueueSnackbar(translateKey('hardcoded.dokument_zostal_usuniety'), {
        variant: 'success',
      });
    },
  });

  const getDocumentActivityId = useCallback(
    (filename: string) =>
      activityItems.find(
        (a) =>
          a.type === VisitActivityResponseDtoTypeEnum.Document &&
          a.action === VisitActivityResponseDtoActionEnum.Added &&
          a.referenceId === filename,
      )?.id,
    [activityItems],
  );

  const assistantLabel = (
    <AiFeatureLabel label={translateKey('visit.vetsiAssistantTab')} />
  );

  const transcriptionLabel = (
    <AiFeatureLabel label={translateKey('visit.transcriptionTab')} />
  );

  // Mirrors the "Notatka" column in Historia wizyt, so the same visit reports
  // the same state in both places.
  const patientNotesStatus = resolveVisitNotesStatus({
    status: visit.status,
    hasNotes: hasRichTextContent(visit.notes),
    hasNotesDraft: !!notesDraft,
  });
  const patientNotesLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {translateKey('visit.visitCourseMenuPatientNotes')}
      {patientNotesStatus && (
        <VisitNotesStatusBadge status={patientNotesStatus} />
      )}
    </Box>
  );

  // Tabs config
  const TABS = [
    // Assistant as the first tab when the side panel doesn't fit (small screens).
    ...(showAssistantTab
      ? [
          {
            value: OrgVisitModalTab.Assistant,
            label: assistantLabel,
            disabled: false,
          },
        ]
      : []),
    {
      value: OrgVisitModalTab.Complaints,
      label: translateKey('visit.complaintsHeading'),
      disabled: false,
    },
    // Show when the visit has activity to display, or when the viewer can edit
    // (then it's the entry point for adding medications/treatments/reminders/
    // documents to an otherwise-empty visit). Hidden for read-only viewers with
    // nothing recorded, so the modal isn't cluttered with an empty tab.
    ...(isVisitCompleted && (activityItems.length > 0 || canEditVisitData)
      ? [
          {
            value: OrgVisitModalTab.Course,
            label: translateKey('visit.visitCourseTitle'),
            disabled: false,
          },
        ]
      : []),
    // Show the transcript tab only when a transcription actually exists.
    ...(hasTranscription
      ? [
          {
            value: OrgVisitModalTab.Transcription,
            label: transcriptionLabel,
            disabled: false,
          },
        ]
      : []),
    // A pending draft is exactly the case where notes exist but haven't been
    // sent, so it opens the tab on its own (whatever the status/role gates say)
    // — otherwise the draft could never be finished from here.
    ...((takesPatientNotes || isDraftPending) &&
    (hasNotes ||
      isDraftPending ||
      isProfessionalRole ||
      isOrganizationAdminRoleSelected)
      ? [
          {
            value: OrgVisitModalTab.PatientNotes,
            label: patientNotesLabel,
            disabled: false,
          },
        ]
      : []),
    // Only show Lekarstwa / Zabiegi / Dokumenty tabs when there is actual data
    // to show. Add/edit flows for empty visits go through the "Przebieg
    // wizyty" tab.
    ...(isVisitCompleted && visitMedications.length > 0
      ? [
          {
            value: OrgVisitModalTab.Medications,
            label: translateKey('visit.tabMedications'),
            disabled: false,
          },
        ]
      : []),
    ...(isVisitCompleted && visitTreatments.length > 0
      ? [
          {
            value: OrgVisitModalTab.Treatments,
            label: translateKey('visit.tabTreatments'),
            disabled: false,
          },
        ]
      : []),
    ...(isVisitCompleted && visitReminders.length > 0
      ? [
          {
            value: OrgVisitModalTab.Reminders,
            label: translateKey('visit.tabReminders'),
            disabled: false,
          },
        ]
      : []),
    ...(isVisitCompleted && visitDocuments.length > 0
      ? [
          {
            value: OrgVisitModalTab.Documents,
            label: 'Dokumenty',
            disabled: false,
          },
        ]
      : []),
    ...(!isVisitRejected &&
    (hasInternalNotes || isProfessionalRole || isOrganizationAdminRoleSelected)
      ? [
          {
            value: OrgVisitModalTab.InternalNotes,
            label: translateKey('visit.tabInternalNotes'),
            disabled: false,
          },
        ]
      : []),
  ];

  return (
    <Stack
      sx={{
        gap: 2,
        [VISIT_MODAL_PAPER_MEDIA]: { flex: '1 0 auto', minHeight: 0 },
      }}
    >
      {/* Always-mounted so the "Dodaj do wizyty" footer button (and its add
          forms) stay available on every detail tab, not just Przebieg wizyty. */}
      <VisitAddMenu
        visitId={visit.id}
        patientId={profileId || ''}
        organizationId={selectedOrganization?.id}
        canAddVisitData={canEditVisitData}
        canAddPatientNotes={canEditPatientNotes}
        canAddInternalNotes={Boolean(canEditInternalNotes)}
        canAddSale={isSameOrg && canStartSale && isVisitSellable(visit.status)}
        hidden={isNotesTab && isEditing}
        onActivityChange={handleActivityChange}
        onAddPatientNotes={() => {
          setValue(OrgVisitModalTab.PatientNotes);
          setIsEditing(true);
        }}
        onAddInternalNotes={() => {
          setValue(OrgVisitModalTab.InternalNotes);
          setIsEditing(true);
        }}
        onAdded={(kind) => {
          const tabByKind = {
            medication: OrgVisitModalTab.Medications,
            treatment: OrgVisitModalTab.Treatments,
            reminder: OrgVisitModalTab.Reminders,
            document: OrgVisitModalTab.Documents,
          } as const;
          setValue(tabByKind[kind]);
        }}
      />
      <Grid
        item
        xs={12}
        sx={{
          // Paper mode: pin the sub-tab bar to the top of the scrolling body so
          // it stays visible (part of the fixed header) while the tab content
          // scrolls underneath. Use background.default — the modal's actual
          // surface colour (MuiPaper override) — so the opaque bar masks the
          // scrolled content without showing a tinted band.
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          [VISIT_MODAL_PAPER_MEDIA]: { position: 'sticky' },
        }}
      >
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderWidth: '1px',
            borderColor: theme.palette.divider,
            borderBottomStyle: 'solid',
            gap: 2,
            mx: { xs: 0, sm: 0, md: 0, lg: 0 },
          }}
        >
          <Tabs
            value={value}
            onChange={(_, newValue) => {
              if (newValue !== value) {
                setIsEditing(false);
              }
              setValue(newValue);
            }}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                disabled={tab.disabled}
                sx={{ fontSize: 16 }}
              />
            ))}
          </Tabs>
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          minHeight: 250,
          display: 'flex',
          flexDirection: 'column',
          // Paper mode: grow to fill the body so the active tab (and any
          // centered empty state) uses the full available height; tall tabs
          // still scroll.
          [VISIT_MODAL_PAPER_MEDIA]: { flex: '1 0 auto' },
        }}
      >
        {value === OrgVisitModalTab.PatientNotes && (
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {isDraftPending && <VisitNotesDraftHint />}
            {isDraftPending && !isEditing ? (
              // Read mode still renders `visit.notes` inside the notes tab, so
              // show the draft text here instead of an empty state.
              <StyledMarkdown>{notesDraft}</StyledMarkdown>
            ) : (
              <OrganizationVisitModalNotesTabs
                visit={visit}
                activeTab={0}
                isEditing={isEditing}
                notes={notes}
                internalNotes={internalNotes}
                onNotesChange={setNotes}
                onInternalNotesChange={setInternalNotes}
              />
            )}
            {isEditing && patientNotesSnapshot !== null && (
              <Box sx={{ mt: 1, display: 'flex' }}>
                <Button
                  onClick={handleRevertPatientNotes}
                  startIcon={<Iconify icon="eva:undo-outline" width={16} />}
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: 13,
                    px: 1.5,
                    minWidth: 0,
                  }}
                >
                  {translateKey('hardcoded.przywroc_poprzednia_wersje')}
                </Button>
              </Box>
            )}
          </Stack>
        )}
        {value === OrgVisitModalTab.InternalNotes && (
          <OrganizationVisitModalNotesTabs
            visit={visit}
            activeTab={1}
            isEditing={isEditing}
            notes={notes}
            internalNotes={internalNotes}
            onNotesChange={setNotes}
            onInternalNotesChange={setInternalNotes}
          />
        )}
        {value === OrgVisitModalTab.Complaints && (
          <Stack sx={{ gap: 2, height: 1 }}>
            <Stack sx={{ gap: 1, minHeight: 100 }}>
              <Typography>{visit.description}</Typography>
            </Stack>
            {bookingDocuments.length > 0 && (
              <Stack sx={{ gap: 1 }}>
                <Typography>
                  {translateKey('quickBooking.attachments')}
                </Typography>
                <Stack sx={{ gap: 2 }}>
                  {bookingDocuments.map((file, index) => (
                    <Stack
                      key={index}
                      sx={{
                        gap: 1.5,
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          padding: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.background.neutral,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {getFileIcon(file.originalFilename || file.filename)}
                      </Box>
                      <Typography
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                        }}
                        noWrap
                        onClick={() => openAttachment(file.filename)}
                      >
                        {file.originalFilename ||
                          file.filename.split('_').slice(2).join('_') ||
                          file.filename}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
        {value === OrgVisitModalTab.Assistant && (
          <Stack sx={{ [VISIT_MODAL_PAPER_MEDIA]: { flex: 1, minHeight: 0 } }}>
            <OrganizationVisitModalAssistant
              visit={visit}
              fillHeight={isPaperMode}
            />
          </Stack>
        )}
        {value === OrgVisitModalTab.Transcription && (
          <Stack>
            <VisitTranscriptionContent
              transcription={transcription}
              newestFirst={false}
              // Let the modal body be the only scroll container — avoids a
              // double scrollbar when the transcript is taller than the body.
              innerScroll={false}
              professional={{
                name: `${visit?.professional?.firstName ?? ''} ${
                  visit?.professional?.lastName ?? ''
                }`.trim(),
                avatar: visit?.professional?.avatar,
              }}
              caregiver={{
                name: `${visit?.user?.firstName ?? ''} ${
                  visit?.user?.lastName ?? ''
                }`.trim(),
                avatar: visit?.user?.avatar,
              }}
            />
          </Stack>
        )}
        {value === OrgVisitModalTab.Forms && (
          <Stack sx={{ gap: 2 }}>
            {isSubmissionsLoading || isFormsLoading ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
              </Stack>
            ) : !hasSubmissions ? (
              <Typography color="text.secondary">
                {translateKey(
                  'hardcoded.brak_wypelnionych_formularzy_dla_tej_wizyty',
                )}
              </Typography>
            ) : (
              submittedSubmissions.map((submission) => {
                const form = formsMap.get(submission.formId);
                if (!form) return null;

                const responseMap = new Map(
                  submission.responses.map((r) => [r.fieldId, r]),
                );

                return (
                  <Stack
                    key={submission.id}
                    sx={{
                      gap: 2,
                      p: 2,
                      bgcolor: 'background.neutral',
                      borderRadius: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1">{form.name}</Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          {format(
                            new Date(submission.updatedAt),
                            'dd.MM.yyyy HH:mm',
                          )}
                        </Typography>
                        <Chip
                          label={translateKey('visit.formStatusSubmitted')}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                    {form.description && (
                      <Typography variant="body2" color="text.secondary">
                        {form.description}
                      </Typography>
                    )}
                    <Divider />
                    {form.fields
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((field) => {
                        const resp = responseMap.get(field.id);
                        const val = resp?.value;

                        if (field.type === FormFieldType.FILE) {
                          const files = Array.isArray(val) ? val : [];
                          return (
                            <Stack key={field.id} sx={{ gap: 0.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {field.label}
                                {field.required && ' *'}
                              </Typography>
                              {files.length === 0 ? (
                                <Typography variant="body2">-</Typography>
                              ) : (
                                <Stack spacing={0.5}>
                                  {files.map(
                                    (
                                      file: {
                                        url: string;
                                        filename: string;
                                        originalFilename?: string;
                                      },
                                      idx: number,
                                    ) => (
                                      <Stack
                                        key={file.filename || idx}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                      >
                                        <Box
                                          sx={{
                                            p: 0.5,
                                            borderRadius: 0.5,
                                            bgcolor: 'background.paper',
                                            display: 'flex',
                                          }}
                                        >
                                          {getFileIcon(
                                            file.originalFilename ||
                                              file.filename,
                                          )}
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                              textDecoration: 'underline',
                                            },
                                          }}
                                          noWrap
                                          onClick={async () => {
                                            if (!file.url) return;
                                            const isFullUrl =
                                              file.url.startsWith('http');
                                            if (isFullUrl) {
                                              window.open(file.url, '_blank');
                                            } else {
                                              try {
                                                const signedUrl =
                                                  await getFileUrl({
                                                    kind: 'visitFile',
                                                    visitId: visit.id,
                                                    filename: file.url,
                                                  });
                                                window.open(
                                                  signedUrl,
                                                  '_blank',
                                                );
                                              } catch {
                                                enqueueSnackbar(
                                                  translateKey(
                                                    'petAi.fileOpenError',
                                                  ),
                                                  { variant: 'error' },
                                                );
                                              }
                                            }
                                          }}
                                        >
                                          {file.originalFilename ||
                                            file.filename}
                                        </Typography>
                                      </Stack>
                                    ),
                                  )}
                                </Stack>
                              )}
                            </Stack>
                          );
                        }

                        let displayValue = '-';
                        if (val !== undefined && val !== null && val !== '') {
                          if (Array.isArray(val)) {
                            displayValue = val
                              .map((v: string) =>
                                v === '__custom__'
                                  ? translateKey('search.specializationOther')
                                  : v,
                              )
                              .join(', ');
                          } else {
                            displayValue =
                              val === '__custom__'
                                ? translateKey('search.specializationOther')
                                : String(val);
                          }
                        }
                        if (resp?.customValue) {
                          displayValue += ` (${resp.customValue})`;
                        }

                        return (
                          <Stack key={field.id} sx={{ gap: 0.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {field.label}
                              {field.required && ' *'}
                            </Typography>
                            <Typography variant="body2">
                              {displayValue}
                            </Typography>
                          </Stack>
                        );
                      })}
                  </Stack>
                );
              })
            )}
          </Stack>
        )}
        {value === OrgVisitModalTab.Course && (
          <VisitModalActivityTab
            items={activityItems}
            canEdit={canEditVisitData}
            visitId={visit.id}
            organizationId={selectedOrganization?.id}
            patientId={profileId}
            singleReminderOnly={!profileId}
            onActivityChange={handleActivityChange}
          />
        )}
        {value === OrgVisitModalTab.Medications && (
          <VisitModalMedicationsTab
            medications={visitMedications}
            canEdit={canEditVisitData}
            canChangeStatus={canChangeMedicationStatus}
            visitId={visit.id}
            patientId={profileId || ''}
            organizationId={selectedOrganization?.id}
            onActivityChange={handleActivityChange}
          />
        )}
        {value === OrgVisitModalTab.Treatments && (
          <VisitModalTreatmentsTab
            treatments={visitTreatments}
            canEdit={canEditVisitData}
            canExportPdf={canExportPdf}
            canSeePrices={canSeePrices}
            visitId={visit.id}
            patientId={profileId || ''}
            organizationId={selectedOrganization?.id}
            organizationName={(visit.organization as any)?.name || ''}
            visitStartTime={visit.startTime}
            onActivityChange={handleActivityChange}
          />
        )}
        {value === OrgVisitModalTab.Documents && (
          <VisitModalDocumentsTab
            documents={visitDocuments}
            canEdit={canEditVisitData}
            visitId={visit.id}
            patientId={profileId || ''}
            onActivityChange={handleActivityChange}
            onDelete={(activityId) => removeActivity(activityId)}
            getActivityIdForDocument={getDocumentActivityId}
          />
        )}
        {value === OrgVisitModalTab.Reminders && (
          <VisitModalRemindersTab
            visitId={visit.id}
            profileId={profileId}
            mutationScope="organization"
          />
        )}
      </Grid>
    </Stack>
  );
}
